import { useMemo, useState } from 'react';

import { RefreshCw, ShoppingCart } from 'lucide-react';

import { FormField, FormInput, FormSelect } from '@/components/form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useConfigurations } from '@/hooks/useConfigurations';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useAssignInboundTwilioNumber,
  useAvailableTwilioNumbers,
  usePurchaseTwilioNumber,
  useSyncTwilioNumbers,
  useTwilioNumbers,
  useUpdateTwilioNumberRegion,
} from '@/hooks/useTwilioNumbers';
import { extractErrorMessage } from '@/lib/utils';

import type { TwilioRegion } from '@repo/shared-types';

export const TwilioNumbersPage = () => {
  usePageTitle('Twilio Numbers');
  const { toast } = useToast();
  const { data: numbers = [], isLoading } = useTwilioNumbers();
  const { data: configurations = [] } = useConfigurations();

  const syncMutation = useSyncTwilioNumbers();
  const assignMutation = useAssignInboundTwilioNumber();
  const purchaseMutation = usePurchaseTwilioNumber();
  const regionMutation = useUpdateTwilioNumberRegion();

  const [countryCode, setCountryCode] = useState('GB');
  const [areaCode, setAreaCode] = useState('');
  const [contains, setContains] = useState('');
  const [limit, setLimit] = useState('20');
  const [selectedNumberToBuy, setSelectedNumberToBuy] = useState('');
  const [assignConfigByNumberId, setAssignConfigByNumberId] = useState<Record<string, string>>({});

  const availableQuery = useMemo(
    () => ({
      country_code: countryCode,
      area_code: areaCode || undefined,
      contains: contains || undefined,
      limit: Number.parseInt(limit, 10) || 20,
    }),
    [areaCode, contains, countryCode, limit],
  );

  const { data: availableNumbers = [], refetch: refetchAvailable } =
    useAvailableTwilioNumbers(availableQuery);

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync();
      toast({
        variant: 'success',
        title: 'Numbers synced',
        description: `${result.count} numbers synced from Twilio.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Sync failed',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleSearchAvailable = async () => {
    const sanitizedContains = contains.trim();
    if (sanitizedContains && sanitizedContains.replace(/[%*]/g, '').length < 2) {
      toast({
        variant: 'destructive',
        title: 'Invalid contains filter',
        description: 'Use at least 2 digits/letters (for example: 55).',
      });
      return;
    }

    try {
      await refetchAvailable();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Search failed',
        description: extractErrorMessage(error),
      });
    }
  };

  const handlePurchase = async () => {
    if (!selectedNumberToBuy) {
      toast({
        variant: 'destructive',
        title: 'Select a number',
        description: 'Choose an available number to buy first.',
      });
      return;
    }

    try {
      await purchaseMutation.mutateAsync({
        phone_number: selectedNumberToBuy,
      });

      setSelectedNumberToBuy('');
      toast({
        variant: 'success',
        title: 'Number purchased',
        description: `${selectedNumberToBuy} has been purchased and saved.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Purchase failed',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleRegionChange = async (
    numberId: string,
    field: 'region' | 'messaging_region',
    value: TwilioRegion,
  ) => {
    try {
      await regionMutation.mutateAsync({
        numberId,
        payload: { [field]: value },
      });
      const label = field === 'region' ? 'Voice region' : 'SMS region';
      toast({
        variant: 'success',
        title: `${label} updated`,
        description: `${label} set to ${value === 'ie1' ? 'EU (Ireland)' : 'US (Virginia)'}.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Region update failed',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleAssignInbound = async (numberId: string) => {
    try {
      const configurationId = assignConfigByNumberId[numberId] || null;
      await assignMutation.mutateAsync({
        numberId,
        payload: {
          configuration_id: configurationId,
        },
      });

      toast({
        variant: 'success',
        title: 'Inbound assignment updated',
        description: configurationId
          ? 'Number is now mapped to the selected configuration.'
          : 'Inbound assignment cleared.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Assignment failed',
        description: extractErrorMessage(error),
      });
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Telephony</p>
          <h1 className="text-3xl font-bold tracking-tight">Twilio Numbers</h1>
          <p className="text-muted-foreground max-w-3xl">
            Manage your number inventory, sync from Twilio, buy new numbers, and assign inbound
            routing to configurations.
          </p>
        </div>
        <Button onClick={handleSync} disabled={syncMutation.isPending}>
          <RefreshCw className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          Sync Numbers
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Buy Number</CardTitle>
          <CardDescription>Search Twilio available numbers and purchase one.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            <FormField label="Country" htmlFor="country_code">
              <FormInput
                id="country_code"
                value={countryCode}
                onChange={(event) => setCountryCode(event.target.value.toUpperCase())}
                maxLength={2}
              />
            </FormField>
            <FormField label="Area code" htmlFor="area_code">
              <FormInput
                id="area_code"
                value={areaCode}
                onChange={(event) => setAreaCode(event.target.value)}
                placeholder="Optional"
              />
            </FormField>
            <FormField label="Contains" htmlFor="contains">
              <FormInput
                id="contains"
                value={contains}
                onChange={(event) => setContains(event.target.value)}
                placeholder="Optional"
              />
            </FormField>
            <FormField label="Limit" htmlFor="limit">
              <FormInput
                id="limit"
                type="number"
                min={1}
                max={50}
                value={limit}
                onChange={(event) => setLimit(event.target.value)}
              />
            </FormField>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <Button type="button" variant="outline" onClick={handleSearchAvailable}>
              Search Available
            </Button>
            <div className="min-w-[320px]">
              <FormField label="Available numbers" htmlFor="available-number">
                <FormSelect
                  id="available-number"
                  value={selectedNumberToBuy}
                  onChange={(event) => setSelectedNumberToBuy(event.target.value)}
                >
                  <option value="">Select number</option>
                  {availableNumbers.map((number) => (
                    <option key={number.phone_number} value={number.phone_number}>
                      {number.phone_number}
                      {number.locality ? ` - ${number.locality}` : ''}
                      {number.region ? `, ${number.region}` : ''}
                    </option>
                  ))}
                </FormSelect>
              </FormField>
            </div>
            <Button type="button" onClick={handlePurchase} disabled={purchaseMutation.isPending}>
              <ShoppingCart className="h-4 w-4" />
              Buy Selected
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inventory</CardTitle>
          <CardDescription>
            Persisted numbers from local database with configuration assignments.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex min-h-[220px] items-center justify-center">
              <div className="border-primary h-10 w-10 animate-spin rounded-full border-b-2" />
            </div>
          ) : numbers.length === 0 ? (
            <p className="text-muted-foreground text-sm">No numbers yet. Sync or buy one.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="border-b px-3 py-2">Name</th>
                    <th className="border-b px-3 py-2">Number</th>
                    <th className="border-b px-3 py-2">Voice Region</th>
                    <th className="border-b px-3 py-2">SMS Region</th>
                    <th className="border-b px-3 py-2">Status</th>
                    <th className="border-b px-3 py-2">Inbound Config</th>
                    <th className="border-b px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {numbers.map((number) => (
                    <tr key={number.id}>
                      <td className="border-b px-3 py-2">{number.friendly_name || '-'}</td>
                      <td className="border-b px-3 py-2 font-mono">{number.phone_number}</td>
                      <td className="border-b px-3 py-2">
                        <FormSelect
                          value={number.region || 'us1'}
                          onChange={(event) =>
                            handleRegionChange(
                              number.id,
                              'region',
                              event.target.value as TwilioRegion,
                            )
                          }
                          disabled={regionMutation.isPending}
                        >
                          <option value="us1">US (Virginia)</option>
                          <option value="ie1">EU (Ireland)</option>
                        </FormSelect>
                      </td>
                      <td className="border-b px-3 py-2">
                        <FormSelect
                          value={number.messaging_region || 'us1'}
                          onChange={(event) =>
                            handleRegionChange(
                              number.id,
                              'messaging_region',
                              event.target.value as TwilioRegion,
                            )
                          }
                          disabled={regionMutation.isPending}
                        >
                          <option value="us1">US (Virginia)</option>
                          <option value="ie1">EU (Ireland)</option>
                        </FormSelect>
                      </td>
                      <td className="border-b px-3 py-2">
                        {number.is_active ? 'Active' : 'Inactive'}
                      </td>
                      <td className="border-b px-3 py-2">
                        <FormSelect
                          value={
                            assignConfigByNumberId[number.id] ??
                            number.inbound_configuration_id ??
                            ''
                          }
                          onChange={(event) =>
                            setAssignConfigByNumberId((prev) => ({
                              ...prev,
                              [number.id]: event.target.value,
                            }))
                          }
                        >
                          <option value="">Unassigned</option>
                          {configurations.map((config) => (
                            <option key={config.id} value={config.id}>
                              {config.name}
                            </option>
                          ))}
                        </FormSelect>
                      </td>
                      <td className="border-b px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAssignInbound(number.id)}
                          disabled={assignMutation.isPending}
                        >
                          Save
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
